import { CSSProperties, useCallback, useMemo } from 'react'
import nanoid from 'nanoid'
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core'
import { Resume } from 'packages/types'
import Anchor from 'builder/components/Anchor'
import SectionComponent, { SectionComponentProps } from 'builder/components/Section'
import { GeneralEditorLevels } from 'builder/modules/generalEditor'
import {
  CardOperationFunc,
  IsCardOpenFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  SortableCard,
  UpdateCardFunc,
} from 'builder/components/Card'
import { isCardEmpty } from 'builder/modules/resumeEditor/resumeSectionCardTrackRules'

type Props = {
  levels?: GeneralEditorLevels
  foldableDuration?: number
  isCardOpen: IsCardOpenFunc
  onCardMove: MoveCardFunc
  onCardDelete: CardOperationFunc
  onCardChange: UpdateCardFunc
  onCardToggle: CardOperationFunc
  onAddCardSubItem?: CardOperationFunc
  onSubItemMove?: MoveSubItemFunc
  isRecommendedLengthHidden?: boolean
} & SectionComponentProps

type SortableProps = Props & { index: number }

export const Section = (props: Props) => {
  const {
    item: section,
    levels,
    locale,
    hideSkillLevel,
    isCardOpen,
    onCardDelete,
    onCardChange,
    onCardToggle,
    foldableDuration,
    template,
    updateSimpleField,
    position,
    isRecommendedLengthHidden,
    onCardMove,
    isStepByStepFlow,
    onAddCardSubItem,
    onSubItemMove,
  } = props
  const { hidden, CardComponent } = section

  const cardsOrder = useMemo(
    () => section.items?.map(item => item.cid || item.id || nanoid()) || [],
    [section.items],
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return

      const oldIndex = cardsOrder.indexOf(active.id)
      const newIndex = cardsOrder.indexOf(over.id)

      onCardMove({ oldIndex, newIndex, sectionId: section.id })
    },
    [onCardMove, cardsOrder, section.id],
  )

  if (hidden) return null

  return (
    <Anchor id={section.id}>
      <SectionComponent {...props} item={section}>
        <DndContext onDragEnd={onDragEnd} collisionDetection={closestCenter}>
          <SortableContext items={cardsOrder} strategy={verticalListSortingStrategy}>
            {section.items?.map((item, index) => {
              const sortId = cardsOrder[index]
              return (
                <SortableCard
                  key={sortId}
                  sortId={sortId}
                  CardComponent={CardComponent}
                  item={item}
                  sectionId={section.id}
                  sectionTitle={section.title}
                  levels={levels}
                  locale={locale}
                  hideSkillLevel={hideSkillLevel}
                  foldableDuration={foldableDuration}
                  isOpen={isCardOpen(sortId, section.id)}
                  onChange={onCardChange}
                  onResumeSimpleFieldChange={updateSimpleField}
                  onDelete={onCardDelete}
                  onToggle={onCardToggle}
                  template={template}
                  jobTitle={position}
                  isRecommendedLengthHidden={isRecommendedLengthHidden}
                  isDeleteVisible={
                    isStepByStepFlow && isCardEmpty(item, section.id as keyof Resume)
                  }
                  possibleSubItems={section.possibleSubItems}
                  onAddSubItem={onAddCardSubItem}
                  onSubItemMove={onSubItemMove}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </SectionComponent>
    </Anchor>
  )
}

export const SortableSection = (props: SortableProps) => {
  const { id, sortId, sortable } = props.item

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId || id,
    disabled: !sortable,
  })

  if (sortable) {
    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
      transition,
      position: 'relative',
      zIndex: isDragging ? 1 : undefined,
    }
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <Section {...props} isDragging={isDragging} dragHandleListeners={listeners} />
      </div>
    )
  }

  return <Section {...props} />
}
